import React from 'react';
import { FormSection } from '@components/form-section';
import { PageHeader } from '@components/page-header';
import { Select } from '@components/select';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import {
    WorkoutCategory,
    WorkoutTypeRuleDto,
    WorkoutTypeV2,
    WorkoutValidationRuleDto
} from '../../core/api/FitnessActivityClient';

export interface WorkoutValidationRuleFormData {
    workoutType: WorkoutTypeV2 | '';
    workoutCategory: WorkoutCategory | '';
    minAverageSpeed: string;
    maxAverageSpeed: string;
    maxAllowedSpeed: string;
    minDurationInMinutes: string;
    minDistanceInMeters: string;
    maxDistanceInMeters: string;
    minCaloriesPerMinute: string;
}

interface WorkoutValidationRuleFormProps {
    title: string;
    submitLabel: string;
    submittingLabel: string;
    initialData?: WorkoutValidationRuleFormData;
    onCancel(): void;
    onSubmit(data: WorkoutValidationRuleFormData): Promise<void>;
}

function formatEnumLabel(value?: string | null): string {
    if (!value) {
        return '-';
    }

    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function parseOptionalFloat(value: string): number | undefined {
    const normalized = value.trim();
    if (!normalized) {
        return undefined;
    }

    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? undefined : parsed;
}

function parseOptionalInt(value: string): number | undefined {
    const normalized = value.trim();
    if (!normalized) {
        return undefined;
    }

    const parsed = parseInt(normalized, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
}

function defaultFormData(): WorkoutValidationRuleFormData {
    return {
        workoutType: '',
        workoutCategory: '',
        minAverageSpeed: '',
        maxAverageSpeed: '',
        maxAllowedSpeed: '',
        minDurationInMinutes: '',
        minDistanceInMeters: '',
        maxDistanceInMeters: '',
        minCaloriesPerMinute: ''
    };
}

function extractErrorMessage(error: unknown, fallback: string): string {
    if (typeof error !== 'object' || !error) {
        return fallback;
    }

    const response = (error as { response?: { data?: { message?: unknown } } })
        .response;
    if (typeof response?.data?.message === 'string') {
        return response.data.message;
    }

    return (error as { message?: string }).message || fallback;
}

function validateForm(data: WorkoutValidationRuleFormData): string | null {
    const minAvgSpeed = parseOptionalFloat(data.minAverageSpeed);
    const maxAvgSpeed = parseOptionalFloat(data.maxAverageSpeed);
    const maxAllowedSpeed = parseOptionalFloat(data.maxAllowedSpeed);
    const minDuration = parseOptionalInt(data.minDurationInMinutes);
    const minDistance = parseOptionalInt(data.minDistanceInMeters);
    const maxDistance = parseOptionalInt(data.maxDistanceInMeters);
    const minCaloriesPerMinute = parseOptionalFloat(data.minCaloriesPerMinute);

    if (
        data.minAverageSpeed &&
        (minAvgSpeed === undefined || minAvgSpeed < 0)
    ) {
        return 'Min Average Speed must be a non-negative number.';
    }
    if (
        data.maxAverageSpeed &&
        (maxAvgSpeed === undefined || maxAvgSpeed < 0)
    ) {
        return 'Max Average Speed must be a non-negative number.';
    }
    if (
        data.maxAllowedSpeed &&
        (maxAllowedSpeed === undefined || maxAllowedSpeed < 0)
    ) {
        return 'Max Allowed Speed must be a non-negative number.';
    }
    if (
        data.minDurationInMinutes &&
        (minDuration === undefined || minDuration < 0)
    ) {
        return 'Min Duration must be a non-negative number.';
    }
    if (
        data.minDistanceInMeters &&
        (minDistance === undefined || minDistance < 0)
    ) {
        return 'Min Distance must be a non-negative number.';
    }
    if (
        data.maxDistanceInMeters &&
        (maxDistance === undefined || maxDistance < 0)
    ) {
        return 'Max Distance must be a non-negative number.';
    }
    if (
        data.minCaloriesPerMinute &&
        (minCaloriesPerMinute === undefined || minCaloriesPerMinute < 0)
    ) {
        return 'Min Calories Per Minute must be a non-negative number.';
    }
    if (
        minAvgSpeed !== undefined &&
        maxAvgSpeed !== undefined &&
        minAvgSpeed > maxAvgSpeed
    ) {
        return 'Min Average Speed cannot be greater than Max Average Speed.';
    }
    if (
        minDistance !== undefined &&
        maxDistance !== undefined &&
        minDistance > maxDistance
    ) {
        return 'Min Distance cannot be greater than Max Distance.';
    }

    return null;
}

export function toWorkoutValidationRuleFormData(
    rule: WorkoutTypeRuleDto
): WorkoutValidationRuleFormData {
    const validationRule = rule.validationRule;

    return {
        workoutType: rule.workoutType || '',
        workoutCategory: rule.workoutCategory || '',
        minAverageSpeed:
            validationRule.minAverageSpeed !== undefined &&
            validationRule.minAverageSpeed !== null
                ? String(validationRule.minAverageSpeed)
                : '',
        maxAverageSpeed:
            validationRule.maxAverageSpeed !== undefined &&
            validationRule.maxAverageSpeed !== null
                ? String(validationRule.maxAverageSpeed)
                : '',
        maxAllowedSpeed:
            validationRule.maxAllowedSpeed !== undefined &&
            validationRule.maxAllowedSpeed !== null
                ? String(validationRule.maxAllowedSpeed)
                : '',
        minDurationInMinutes:
            validationRule.minDurationInMinutes !== undefined &&
            validationRule.minDurationInMinutes !== null
                ? String(validationRule.minDurationInMinutes)
                : '',
        minDistanceInMeters:
            validationRule.minDistanceInMeters !== undefined &&
            validationRule.minDistanceInMeters !== null
                ? String(validationRule.minDistanceInMeters)
                : '',
        maxDistanceInMeters:
            validationRule.maxDistanceInMeters !== undefined &&
            validationRule.maxDistanceInMeters !== null
                ? String(validationRule.maxDistanceInMeters)
                : '',
        minCaloriesPerMinute:
            validationRule.minCaloriesPerMinute !== undefined &&
            validationRule.minCaloriesPerMinute !== null
                ? String(validationRule.minCaloriesPerMinute)
                : ''
    };
}

export function toWorkoutValidationRulePayload(
    formData: WorkoutValidationRuleFormData
): {
    workoutType?: WorkoutTypeV2;
    workoutCategory?: WorkoutCategory;
    validationRule: WorkoutValidationRuleDto;
} {
    return {
        workoutType: formData.workoutType
            ? (formData.workoutType as WorkoutTypeV2)
            : undefined,
        workoutCategory: formData.workoutCategory
            ? (formData.workoutCategory as WorkoutCategory)
            : undefined,
        validationRule: {
            minAverageSpeed: parseOptionalFloat(formData.minAverageSpeed),
            maxAverageSpeed: parseOptionalFloat(formData.maxAverageSpeed),
            maxAllowedSpeed: parseOptionalFloat(formData.maxAllowedSpeed),
            minDurationInMinutes: parseOptionalInt(
                formData.minDurationInMinutes
            ),
            minDistanceInMeters: parseOptionalInt(formData.minDistanceInMeters),
            maxDistanceInMeters: parseOptionalInt(formData.maxDistanceInMeters),
            minCaloriesPerMinute: parseOptionalFloat(
                formData.minCaloriesPerMinute
            )
        }
    };
}

export const WorkoutValidationRuleForm: React.FC<
    WorkoutValidationRuleFormProps
> = ({
    title,
    submitLabel,
    submittingLabel,
    initialData,
    onCancel,
    onSubmit
}) => {
    const [formData, setFormData] =
        React.useState<WorkoutValidationRuleFormData>(
            initialData || defaultFormData()
        );
    const [error, setError] = React.useState<string | null>(null);
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        setFormData(initialData || defaultFormData());
        setError(null);
    }, [initialData]);

    const handleFieldChange = (
        field: keyof WorkoutValidationRuleFormData,
        value: string
    ) => {
        setFormData((current) => ({
            ...current,
            [field]: value
        }));
        if (error) {
            setError(null);
        }
    };

    const handleSubmit = async () => {
        const validationError = validateForm(formData);
        if (validationError) {
            setError(validationError);
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await onSubmit(formData);
        } catch (submitError) {
            setError(
                extractErrorMessage(
                    submitError,
                    'Failed to save workout validation rule.'
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    const noneOption = { label: 'None', value: '' };

    return (
        <>
            <PageHeader title={title} />

            <div className="flex flex-col gap-6 pb-24">
                <FormSection
                    title="Rule Scope"
                    description="Both fields are optional. Set either or both to narrow the rule scope."
                >
                    <div className="grid gap-4 md:grid-cols-2">
                        <Select
                            label="Workout Category"
                            value={formData.workoutCategory}
                            disabled={submitting}
                            options={[
                                noneOption,
                                ...Object.values(WorkoutCategory).map(
                                    (category) => ({
                                        label: formatEnumLabel(category),
                                        value: category
                                    })
                                )
                            ]}
                            onChange={(value: string) =>
                                handleFieldChange('workoutCategory', value)
                            }
                        />

                        <Select
                            label="Workout Type"
                            value={formData.workoutType}
                            disabled={submitting}
                            options={[
                                noneOption,
                                ...Object.values(WorkoutTypeV2).map((type) => ({
                                    label: formatEnumLabel(type),
                                    value: type
                                }))
                            ]}
                            onChange={(value: string) =>
                                handleFieldChange('workoutType', value)
                            }
                        />
                    </div>
                </FormSection>

                <FormSection
                    title="Speed"
                    description="All fields are optional. Only populated thresholds will be enforced."
                >
                    <div className="grid gap-4 md:grid-cols-2">
                        <TextField
                            type="number"
                            label="Min Average Speed (km/h)"
                            value={formData.minAverageSpeed}
                            disabled={submitting}
                            onChange={(value) =>
                                handleFieldChange(
                                    'minAverageSpeed',
                                    String(value)
                                )
                            }
                        />
                        <TextField
                            type="number"
                            label="Max Average Speed (km/h)"
                            value={formData.maxAverageSpeed}
                            disabled={submitting}
                            onChange={(value) =>
                                handleFieldChange(
                                    'maxAverageSpeed',
                                    String(value)
                                )
                            }
                        />
                        <TextField
                            type="number"
                            label="Max Allowed Speed (km/h)"
                            value={formData.maxAllowedSpeed}
                            disabled={submitting}
                            onChange={(value) =>
                                handleFieldChange(
                                    'maxAllowedSpeed',
                                    String(value)
                                )
                            }
                        />
                    </div>
                </FormSection>

                <FormSection title="Duration and Distance">
                    <div className="grid gap-4 md:grid-cols-2">
                        <TextField
                            type="number"
                            label="Min Duration (minutes)"
                            value={formData.minDurationInMinutes}
                            disabled={submitting}
                            onChange={(value) =>
                                handleFieldChange(
                                    'minDurationInMinutes',
                                    String(value)
                                )
                            }
                        />
                        <TextField
                            type="number"
                            label="Min Distance (meters)"
                            value={formData.minDistanceInMeters}
                            disabled={submitting}
                            onChange={(value) =>
                                handleFieldChange(
                                    'minDistanceInMeters',
                                    String(value)
                                )
                            }
                        />
                        <TextField
                            type="number"
                            label="Max Distance (meters)"
                            value={formData.maxDistanceInMeters}
                            disabled={submitting}
                            onChange={(value) =>
                                handleFieldChange(
                                    'maxDistanceInMeters',
                                    String(value)
                                )
                            }
                        />
                    </div>
                </FormSection>

                <FormSection title="Calories">
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="flex flex-col gap-1.5">
                            <TextField
                                type="number"
                                label="Min Calories Per Minute"
                                value={formData.minCaloriesPerMinute}
                                disabled={submitting}
                                onChange={(value) =>
                                    handleFieldChange(
                                        'minCaloriesPerMinute',
                                        String(value)
                                    )
                                }
                            />
                            <p className="text-xs text-muted-foreground">
                                Use this for generic floors like 1 kcal/min on a
                                workout category.
                            </p>
                        </div>
                    </div>
                </FormSection>

                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
            </div>

            <StickyBar>
                <Button
                    onClick={onCancel}
                    variant="ghost"
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button onClick={handleSubmit} loading={submitting}>
                    {submitting ? submittingLabel : submitLabel}
                </Button>
            </StickyBar>
        </>
    );
};
